import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';

import { OuterLink } from '@shared/ui/GenericLink';
import { CheckboxField } from '@shared/ui/CheckboxField';

import { Element } from '@shared/interfaces/element.interface';

import { useLanguage } from '@hooks/useLanguage';
import { Shop } from '@hooks/useIntegrationConfigs';

interface ShopOfferAgreementCheckboxProps extends Element {
  id: string;

  shop: Shop;

  onIsApprovedChange: (approved: boolean) => void;
}

function useIsApprovedForm(onIsApprovedChange: (approved: boolean) => void) {
  const methods = useForm({
    defaultValues: {
      isApproved: false,
    },
  });
  const { watch, getValues } = methods;

  useEffect(() => {
    onIsApprovedChange(getValues().isApproved);
  }, [watch('isApproved')]);

  return methods;
}

const useComponentState = ({ id, className, shop, onIsApprovedChange }: ShopOfferAgreementCheckboxProps) => {
  const language = useLanguage();
  const formMethods = useIsApprovedForm(onIsApprovedChange);

  const link = useMemo(() => {
    if (!shop || !shop.config.offerAggreement) {
      return `/`;
    }

    return `/documents/${shop.config.offerAggreement}${language === 'uk' ? '' : '-en'}.pdf`;
  }, [language, shop]);

  return {
    id,
    className,
    link,
    shop,
    formMethods,
  };
};

export const ShopOfferAgreementCheckbox: React.FC<ShopOfferAgreementCheckboxProps> = (props) => {
  const { t } = useTranslation();
  const { id, className, formMethods, shop, link } = useComponentState(props);

  return (
    <div className={className}>
      <FormProvider {...formMethods}>
        <CheckboxField
          id={id}
          name="isApproved"
          label={
            <>
              {t('agree-with')}{' '}
              <OuterLink className="text-shamrock hover:underline" to={link}>
                {t('public-contract')}
              </OuterLink>
            </>
          }
        />
      </FormProvider>
    </div>
  );
};
